import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebaseConfig';
import {
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
} from 'firebase/firestore';

const FollowButton = ({ targetUserId, initialIsFollowing = false, onFollowChange, size = 'default' }) => {
  const [isFollowing, setIsFollowing] = useState(initialIsFollowing);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setIsFollowing(initialIsFollowing);
  }, [initialIsFollowing]);

  const handleFollow = async () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      navigate('/login');
      return;
    }

    if (currentUser.uid === targetUserId) {
      return; // Can't follow yourself
    }

    setLoading(true);
    try {
      const currentUserRef = doc(db, 'profiles', currentUser.uid);
      const targetUserRef = doc(db, 'profiles', targetUserId);

      if (isFollowing) {
        // Unfollow
        await updateDoc(currentUserRef, {
          following: arrayRemove(targetUserId)
        });
        await updateDoc(targetUserRef, {
          followers: arrayRemove(currentUser.uid)
        });
        setIsFollowing(false);
      } else {
        // Follow
        await updateDoc(currentUserRef, {
          following: arrayUnion(targetUserId)
        });
        await updateDoc(targetUserRef, {
          followers: arrayUnion(currentUser.uid)
        });
        setIsFollowing(true);
      }

      // Notify parent component of the change
      if (onFollowChange) {
        onFollowChange(isFollowing);
      }
    } catch (error) {
      console.error('Error updating follow status:', error);
    } finally {
      setLoading(false);
    }
  };

  const buttonClasses = `
    ${size === 'small' ? 'px-3 py-1 text-sm' : 'px-6 py-2'} 
    rounded-lg transition-colors 
    ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
    ${isFollowing 
      ? 'bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400' 
      : 'bg-purple-600 text-white hover:bg-purple-700'
    }
  `;

  return (
    <button
      onClick={handleFollow}
      disabled={loading}
      className={buttonClasses}
    >
      {loading ? (
        <span className="flex items-center gap-2">
          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          {isFollowing ? 'Unfollowing...' : 'Following...'}
        </span>
      ) : (
        <span>
          {isFollowing ? 'Following' : 'Follow'}
        </span>
      )}
    </button>
  );
};

export default FollowButton;
